import { useQuery } from '@tanstack/react-query';

import envConfig from '../config';

export type StateCountyMapResponse = {
  response: Record<string, string[]>;
};

// Schema originally defined the URL as
//   https://queries.api.legalshieldproviders.com/v1/public/queries/lookups/languages
// The library strips the `*.com` host and rebinds the path to the env-aware
// `queriesServiceUrl`, so dev/uat/prod all hit the correct host.
const LANGUAGES_PATH = '/v1/public/queries/lookups/languages';

export type ZipCodeResponse = {
  city: string;
  country: string;
  county: string;
  stateOrProvince: string;
  zipCode: string;
};

export const fetchZipCodeData = async (zipCode: string): Promise<ZipCodeResponse | null> => {
  const trimmed = zipCode.toUpperCase().trim();
  const canadaShortCode = trimmed.length === 7 ? trimmed.slice(0, 3) : null;
  const url = `${envConfig.queriesServiceUrl}/v1/public/queries/queries/getZipCodeDataByZipCode?zipCode=${
    canadaShortCode ?? trimmed
  }`;
  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) return null;
  const data = (await resp.json()) as ZipCodeResponse[];
  return data?.length ? data[0] : null;
};

type StatesByCountryResponse = {
  CountryCode: string;
  State: string;
};

export const useStatesByCountryCode = (countryCode: string | undefined) =>
  useQuery({
    enabled: !!countryCode,
    queryKey: ['rnq-states', countryCode],
    queryFn: async () => {
      const url = `${
        envConfig.queriesServiceUrl
      }/v1/public/queries/lookups/statesByCountryCodes?codes=${encodeURIComponent(JSON.stringify([countryCode]))}`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(`Failed to load states (${resp.status})`);
      return (await resp.json()) as StatesByCountryResponse[];
    },
  });

export const useCountiesByStates = (stateCodes: string[]) =>
  useQuery({
    enabled: stateCodes.length > 0,
    queryKey: ['rnq-counties', ...stateCodes.slice().sort()],
    queryFn: async () => {
      const param = encodeURIComponent(JSON.stringify(stateCodes));
      const url = `${envConfig.queriesServiceUrl}/v1/public/queries/queries/getStateCountyMap?states=${param}`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(`Failed to load counties (${resp.status})`);
      return (await resp.json()) as StateCountyMapResponse[];
    },
  });

const titleCaseLanguage = (raw: string) => {
  const normalized = raw.charAt(0) + raw.slice(1).toLowerCase();
  return normalized.replace(/_/g, ' ');
};

export const useLanguages = () =>
  useQuery({
    queryKey: ['rnq-languages'],
    queryFn: async () => {
      const url = `${envConfig.queriesServiceUrl}${LANGUAGES_PATH}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Failed to load languages (${resp.status})`);
      const data = (await resp.json()) as { Languages: string[] }[];
      const list = data?.[0]?.Languages ?? [];
      return list.filter((lang) => lang !== 'ENGLISH').map((lang) => ({ title: titleCaseLanguage(lang), value: lang }));
    },
  });
